import React, { useState, useEffect } from 'react';
import { StyleSheet, View, Text, TouchableOpacity, Image, ScrollView, Modal, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import ImageZoom from 'react-native-image-pan-zoom';
import { LinearGradient } from 'expo-linear-gradient';

const InteractiveMapView = ({ 
  tilesUrl, 
  ndviTilesUrl, 
  ndreTilesUrl, 
  gndviTilesUrl, 
  fieldGeojson 
}) => {
  const [activeLayer, setActiveLayer] = useState('overview');
  const [imageUrls, setImageUrls] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [zoomImageUrl, setZoomImageUrl] = useState(null);
  
  const screenData = Dimensions.get('window');
  
  useEffect(() => {
    if (tilesUrl && tilesUrl.includes('missions/mission-')) {
      const match = tilesUrl.match(/(https:\/\/[^/]+\/missions\/mission-\d+)/);
      
      if (match) {
        const baseUrl = match[1];
        
        // Extraer mission ID
        const missionMatch = baseUrl.match(/mission-(\d+)/);
        const missionId = missionMatch ? missionMatch[1] : null;
        
        if (missionId) {
          // Detectar entorno y configurar URL base correcta
          let apiBase;
          if (process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost') {
            // Producción: usar la URL del backend en producción
            apiBase = 'https://amissionhub.com';
          } else {
            // Desarrollo: usar localhost
            apiBase = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api/v1', '') : 'http://localhost:8000';
          }
          
          console.log('Construyendo URLs de imágenes:');
          console.log('Entorno:', process.env.NODE_ENV);
          console.log('Hostname:', window.location.hostname);
          console.log('API Base:', apiBase);
          console.log('Mission ID:', missionId);
          const timestamp = Date.now();
          const urls = {
            orthomosaic: `${apiBase}/api/v1/tif-to-png/${missionId}/result.tif/?t=${timestamp}`,
            ndvi: `${apiBase}/api/v1/tif-to-png/${missionId}/NDVI.tif/?t=${timestamp}`,
            ndre: `${apiBase}/api/v1/tif-to-png/${missionId}/NDRE.tif/?t=${timestamp}`,
            gndvi: `${apiBase}/api/v1/tif-to-png/${missionId}/GNDVI.tif/?t=${timestamp}`,
            osavi: `${apiBase}/api/v1/tif-to-png/${missionId}/OSAVI.tif/?nocache=${Math.random()}&t=${timestamp}`
          };
          
          console.log('URLs generadas:', urls);
          setImageUrls(urls);
        }
      }
    }
  }, [tilesUrl]);

  const getLayerInfo = (layer) => {
    switch(layer) {
      case 'orthomosaic':
        return {
          title: 'Ortomosaico HD',
          description: 'Imagen de alta resolución del campo completo con detalles estructurales y patrones generales.',
          color: '#3498db',
          icon: 'layers-outline',

          colorScale: null
        };
      case 'ndvi':
        return {
          title: 'Índice NDVI',
          description: 'Índice de vegetación que muestra la salud general de las plantas. Valores altos indican vegetación saludable.',
          color: '#27ae60',
          icon: 'leaf-outline',

          colorScale: [
            { color: '#000080', value: '-1', label: 'Agua/Sombras' },
            { color: '#0000FF', value: '-0.5', label: 'Suelo desnudo' },
            { color: '#00FFFF', value: '0', label: 'Vegetación mínima' },
            { color: '#00FF00', value: '0.2', label: 'Vegetación ligera' },
            { color: '#FFFF00', value: '0.4', label: 'Vegetación moderada' },
            { color: '#FFA500', value: '0.6', label: 'Vegetación densa' },
            { color: '#FF0000', value: '0.8', label: 'Vegetación muy densa' },
            { color: '#FF00FF', value: '1', label: 'Vegetación óptima' }
          ]
        };
      case 'ndre':
        return {
          title: 'Índice NDRE',
          description: 'Índice de Red Edge normalizado que detecta estrés temprano en plantas y deficiencias de nitrógeno. Valores entre -1 y 1.',
          color: '#e67e22',
          icon: 'pulse-outline',

          colorScale: [
            { color: '#000080', value: '-1', label: 'Agua/Sombras' },
            { color: '#0040FF', value: '-0.5', label: 'Suelo desnudo' },
            { color: '#00FFFF', value: '0', label: 'Vegetación mínima' },
            { color: '#00FF80', value: '0.2', label: 'Vegetación baja' },
            { color: '#80FF00', value: '0.4', label: 'Vegetación moderada' },
            { color: '#FFFF00', value: '0.6', label: 'Vegetación saludable' },
            { color: '#FF8000', value: '0.8', label: 'Vegetación densa' },
            { color: '#FF0000', value: '1', label: 'Vegetación óptima' }
          ]
        };
      case 'gndvi':
        return {
          title: 'Índice GNDVI',
          description: 'Variante del NDVI que usa la banda verde, especialmente útil para cultivos densos y análisis de clorofila.',
          color: '#2ecc71',
          icon: 'flower-outline',

          colorScale: [
            { color: '#000080', value: '-1', label: 'Agua/Sombras' },
            { color: '#0000FF', value: '-0.5', label: 'Suelo desnudo' },
            { color: '#00FFFF', value: '0', label: 'Vegetación mínima' },
            { color: '#00FF00', value: '0.2', label: 'Vegetación ligera' },
            { color: '#FFFF00', value: '0.4', label: 'Vegetación moderada' },
            { color: '#FFA500', value: '0.6', label: 'Vegetación densa' },
            { color: '#FF0000', value: '0.8', label: 'Vegetación muy densa' },
            { color: '#FF00FF', value: '1', label: 'Vegetación óptima' }
          ]
        };
      case 'osavi':
        return {
          title: 'Índice OSAVI',
          description: 'Índice de Vegetación Ajustado por Suelo Optimizado, reduce la influencia del fondo del suelo en el análisis.',
          color: '#9b59b6',
          icon: 'earth-outline',
          colorScale: [
            { color: '#404040', value: '30%', label: 'Suelo desnudo' },
            { color: '#808080', value: '50%', label: 'Suelo con cobertura mínima' },
            { color: '#C0C0C0', value: '85%', label: 'Vegetación moderada' },
            { color: '#B478C8', value: '2%', label: 'Vegetación densa' }
          ]
        };
      default:
        return null;
    }
  };

  const renderColorScale = (colorScale) => {
    if (!colorScale) return null;
    
    return (
      <View style={styles.colorScaleContainer}>
        <Text style={styles.colorScaleTitle}>Escala de Colores</Text>
        <View style={styles.colorScaleBar}>
          {colorScale.map((item, index) => (
            <View key={index} style={styles.colorScaleItem}>
              <View style={[styles.colorBox, { backgroundColor: item.color }]} />
              <Text style={styles.colorLabel}>{item.label}</Text>
              {item.value && <Text style={styles.colorValue}>{item.value}</Text>}
            </View>
          ))}
        </View>
      </View>
    );
  };

  const openZoomModal = (imageUrl) => {
    setZoomImageUrl(imageUrl);
    setShowZoomModal(true);
  };

  const renderAnalysisDetail = () => {
    if (activeLayer === 'overview') return null;
    
    const layerInfo = getLayerInfo(activeLayer);
    const imageUrl = imageUrls[activeLayer];
    
    if (!layerInfo) return null;

    return (
      <View style={styles.detailContainer}>
        <View style={styles.detailHeader}>
          <TouchableOpacity 
            style={styles.backButton}
            onPress={() => setActiveLayer('overview')}
          >
            <Ionicons name="arrow-back" size={24} color="#3498db" />
            <Text style={styles.backButtonText}>Volver al análisis</Text>
          </TouchableOpacity>
        </View>
        
        <ScrollView style={styles.detailContent} showsVerticalScrollIndicator={false}>
          <View style={styles.analysisHeader}>
            <View style={[styles.analysisIcon, { backgroundColor: layerInfo.color }]}>
              <Ionicons name={layerInfo.icon} size={32} color="white" />
            </View>
            <Text style={styles.analysisTitle}>{layerInfo.title}</Text>
            <Text style={styles.analysisDescription}>{layerInfo.description}</Text>
          </View>

          <View style={styles.imageSection}>
            <Text style={styles.sectionTitle}>Vista Previa del Análisis</Text>
            <View style={styles.imageContainer}>
              {imageErrors[activeLayer] ? (
                <View style={styles.imagePlaceholder}>
                  <Ionicons name={layerInfo.icon} size={48} color={layerInfo.color} />
                  <Text style={[styles.placeholderTitle, { color: layerInfo.color }]}>{layerInfo.title}</Text>
                  <Text style={styles.placeholderSubtext}>Vista previa no disponible</Text>
                  <Text style={styles.placeholderNote}>Ejecuta: python convert_tif_to_png.py {tilesUrl.match(/mission-(\d+)/)?.[1] || 'X'}</Text>
                </View>
              ) : (
                <TouchableOpacity onPress={() => openZoomModal(imageUrl)}>
                  <Image
                    source={{ uri: imageUrl }}
                    style={styles.analysisImage}
                    resizeMode="contain"
                    onLoad={() => {
                      console.log('Imagen cargada exitosamente:', imageUrl);
                    }}
                    onError={(error) => {
                      console.log('Error cargando imagen:', imageUrl, error);
                      setImageErrors(prev => ({ ...prev, [activeLayer]: true }));
                    }}
                    onLoadStart={() => {
                      console.log('Iniciando carga de imagen:', imageUrl);
                    }}
                  />
                  {layerInfo.colorScale && (
                    <View style={styles.colorScaleOverlay}>
                      <View style={styles.colorScaleContainer}>
                        <Text style={styles.colorScaleLabelSide}>Alto</Text>
                        <LinearGradient
                          colors={layerInfo.colorScale.slice().reverse().map(item => item.color)}
                          style={styles.colorScaleBar}
                          start={{ x: 0, y: 0 }}
                          end={{ x: 0, y: 1 }}
                        />
                        <Text style={styles.colorScaleLabelSide}>Bajo</Text>
                      </View>
                    </View>
                  )}
                  <View style={styles.zoomIndicator}>
                    <Ionicons name="expand-outline" size={20} color="white" />
                    <Text style={styles.zoomText}>Toca para zoom</Text>
                  </View>
                </TouchableOpacity>
              )}
            </View>
            <Text style={styles.imageCaption}>
              Análisis {layerInfo.title} - Generado desde {activeLayer.toUpperCase()}.tif
            </Text>
            <View style={styles.imageNote}>
              <Ionicons name="information-circle-outline" size={14} color="#f39c12" />
              <Text style={styles.imageNoteText}>
                <Text style={styles.imageNoteBold}>Nota:</Text> Las áreas transparentes (sin color) representan zonas fuera del análisis. 
                Los valores negativos (azul/morado) indican agua, sombras o suelo desnudo.
              </Text>
            </View>
          </View>

          <View style={styles.interpretationSection}>
            <Text style={styles.sectionTitle}>Cómo Interpretar este Gráfico</Text>
            {activeLayer === 'ndre' && (
              <View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Zonas verdes brillantes:</Text> Vegetación saludable con buen contenido de clorofila y sin estrés.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Zonas verdes claras:</Text> Vegetación moderada, posible inicio de estrés leve.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Zonas cian/azul claro:</Text> Vegetación mínima o con estrés moderado.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Zonas azules/moradas:</Text> Suelo desnudo, agua o áreas sin vegetación.
                  </Text>
                </View>
              </View>
            )}
            {activeLayer === 'ndvi' && (
              <View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Magenta (1.0):</Text> Vegetación óptima con máxima densidad y salud.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Rojo (0.8):</Text> Vegetación muy densa y saludable.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Naranja (0.6):</Text> Vegetación densa con buena salud.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Amarillo (0.4):</Text> Vegetación moderada.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Verde (0.2):</Text> Vegetación ligera o con estrés.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Cian (0):</Text> Vegetación mínima.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Azul/Morado (negativo):</Text> Agua, sombras o suelo desnudo.
                  </Text>
                </View>
              </View>
            )}
            {activeLayer === 'gndvi' && (
              <View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Magenta (1.0):</Text> Vegetación óptima con máximo contenido de clorofila.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Rojo (0.8):</Text> Vegetación muy densa y saludable.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Naranja (0.6):</Text> Vegetación densa con buen contenido de clorofila.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Amarillo (0.4):</Text> Vegetación moderada.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Verde (0.2):</Text> Vegetación ligera.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Cian (0):</Text> Vegetación mínima.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Azul (negativo):</Text> Agua, sombras o suelo desnudo.
                  </Text>
                </View>
              </View>
            )}
            {activeLayer === 'osavi' && (
              <View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Gris oscuro:</Text> Suelo desnudo o con muy poca cobertura vegetal.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Gris medio:</Text> Suelo con cobertura vegetal mínima o dispersa.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Gris claro:</Text> Vegetación moderada con influencia del suelo.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Morado (escaso):</Text> Vegetación densa con mínima influencia del suelo.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Ventaja OSAVI:</Text> Reduce la interferencia del suelo en el análisis, ideal para cultivos jóvenes o con cobertura parcial.
                  </Text>
                </View>
              </View>
            )}
            {activeLayer === 'orthomosaic' && (
              <View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Imagen base:</Text> Muestra el campo tal como lo vería el ojo humano, útil para identificar estructuras y patrones.
                  </Text>
                </View>
                <View style={styles.interpretationItem}>
                  <Ionicons name="information-circle" size={16} color="#3498db" />
                  <Text style={styles.interpretationText}>
                    <Text style={styles.interpretationBold}>Referencia visual:</Text> Combina con los índices espectrales para correlacionar problemas visibles.
                  </Text>
                </View>
              </View>
            )}
          </View>

          <View style={styles.fileInfoSection}>
            <Text style={styles.sectionTitle}>Información del Archivo</Text>
            <View style={styles.fileInfoRow}>
              <Ionicons name="document-outline" size={16} color="#7f8c8d" />
              <Text style={styles.fileInfoText}>Nombre: {activeLayer.toUpperCase()}.tif</Text>
            </View>
            <View style={styles.fileInfoRow}>
              <Ionicons name="cloud-outline" size={16} color="#3498db" />
              <Text style={styles.fileInfoText}>Ubicación: AWS S3</Text>
            </View>
            <View style={styles.fileInfoRow}>
              <Ionicons name="checkmark-circle-outline" size={16} color="#27ae60" />
              <Text style={styles.fileInfoText}>Estado: Procesado y disponible</Text>
            </View>

          </View>
        </ScrollView>
      </View>
    );
  };

  const renderOverview = () => (
    <ScrollView style={styles.overviewContainer} showsVerticalScrollIndicator={false}>
      <View style={styles.layersGrid}>
        {Object.keys(imageUrls).map((layerKey) => {
          const layerInfo = getLayerInfo(layerKey);
          if (!layerInfo) return null;
          
          return (
            <TouchableOpacity 
              key={layerKey}
              style={styles.layerCard}
              onPress={() => setActiveLayer(layerKey)}
            >
              <View style={[styles.layerCardIcon, { backgroundColor: layerInfo.color }]}>
                <Ionicons name={layerInfo.icon} size={24} color="white" />
              </View>
              <Text style={styles.layerCardTitle}>{layerInfo.title}</Text>
              <Text style={styles.layerCardDesc} numberOfLines={2}>
                {layerInfo.description.split('.')[0]}
              </Text>
              
              <View style={styles.layerCardFooter}>
                <View style={styles.layerCardStatus}>
                  <Ionicons name="checkmark-circle" size={14} color="#27ae60" />
                  <Text style={styles.layerCardStatusText}>Disponible</Text>
                </View>
                <Ionicons name="chevron-forward" size={16} color="#bdc3c7" />
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
      
      <View style={styles.summarySection}>
        <Text style={styles.summaryTitle}>Resumen del Procesamiento</Text>
        <View style={styles.summaryRow}>
          <Ionicons name="document-outline" size={16} color="#7f8c8d" />
          <Text style={styles.summaryText}>Archivos TIF procesados: {Object.keys(imageUrls).length}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Ionicons name="checkmark-circle-outline" size={16} color="#27ae60" />
          <Text style={styles.summaryText}>Todos los análisis completados exitosamente</Text>
        </View>
        <View style={styles.summaryRow}>
          <Ionicons name="cloud-outline" size={16} color="#3498db" />
          <Text style={styles.summaryText}>Archivos almacenados en AWS S3</Text>
        </View>
      </View>
    </ScrollView>
  );

  return (
    <View style={styles.container}>
      {activeLayer === 'overview' ? renderOverview() : renderAnalysisDetail()}
      
      <Modal
        visible={showZoomModal}
        transparent={true}
        onRequestClose={() => setShowZoomModal(false)}
      >
        <View style={styles.zoomModalContainer}>
          <TouchableOpacity 
            style={styles.zoomCloseButton}
            onPress={() => setShowZoomModal(false)}
          >
            <Ionicons name="close" size={30} color="white" />
          </TouchableOpacity>
          
          {zoomImageUrl && (
            <View style={{ flex: 1 }}>
              <ImageZoom
                cropWidth={screenData.width}
                cropHeight={screenData.height}
                imageWidth={screenData.width}
                imageHeight={screenData.height * 0.8}
                enableSwipeDown={true}
                onSwipeDown={() => setShowZoomModal(false)}
              >
                <Image
                  source={{ uri: zoomImageUrl }}
                  style={{
                    width: screenData.width,
                    height: screenData.height * 0.8
                  }}
                  resizeMode="contain"
                />
              </ImageZoom>
              
              {getLayerInfo(activeLayer)?.colorScale && (
                <View style={styles.colorScaleOverlayZoom}>
                  <View style={styles.colorScaleContainer}>
                    <Text style={styles.colorScaleLabelSide}>Alto</Text>
                    <LinearGradient
                      colors={getLayerInfo(activeLayer).colorScale.slice().reverse().map(item => item.color)}
                      style={styles.colorScaleBar}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 0, y: 1 }}
                    />
                    <Text style={styles.colorScaleLabelSide}>Bajo</Text>
                  </View>
                </View>
              )}
            </View>
          )}
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: 400,
    borderRadius: 8,
    overflow: 'hidden',
    marginVertical: 10,
  },
  overviewContainer: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    paddingTop: 15,
  },
  layersGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    marginBottom: 15,
  },
  layerCard: {
    width: '48%',
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#ecf0f1',
  },
  layerCardIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  layerCardTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 4,
  },
  layerCardDesc: {
    fontSize: 11,
    color: '#7f8c8d',
    lineHeight: 16,
    marginBottom: 8,
  },
  layerCardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  layerCardStatus: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  layerCardStatusText: {
    fontSize: 11,
    color: '#27ae60',
    marginLeft: 4,
    fontWeight: '500',
  },
  summarySection: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 15,
    marginHorizontal: 15,
    marginBottom: 15,
  },
  summaryTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
  },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  summaryText: {
    fontSize: 13,
    color: '#34495e',
    marginLeft: 8,
  },
  detailContainer: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  detailHeader: {
    backgroundColor: 'white',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#ecf0f1',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButtonText: {
    fontSize: 16,
    color: '#3498db',
    marginLeft: 8,
    fontWeight: '500',
  },
  detailContent: {
    flex: 1,
  },
  analysisHeader: {
    alignItems: 'center',
    padding: 20,
    backgroundColor: 'white',
    marginBottom: 10,
  },
  analysisIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  analysisTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 8,
  },
  analysisDescription: {
    fontSize: 14,
    color: '#34495e',
    textAlign: 'center',
    lineHeight: 20,
  },
  imageSection: {
    backgroundColor: 'white',
    padding: 15,
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
  },
  imageContainer: {
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
    marginBottom: 10,
  },
  analysisImage: {
    width: '100%',
    height: 200,
  },
  imageCaption: {
    fontSize: 12,
    color: '#7f8c8d',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  imageNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#fff3cd',
    padding: 10,
    borderRadius: 6,
    marginTop: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#f39c12',
  },
  imageNoteText: {
    fontSize: 12,
    color: '#856404',
    marginLeft: 6,
    flex: 1,
    lineHeight: 16,
  },
  imageNoteBold: {
    fontWeight: 'bold',
  },
  imagePlaceholder: {
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '#ecf0f1',
  },
  placeholderTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 8,
  },
  placeholderSubtext: {
    fontSize: 14,
    color: '#7f8c8d',
    marginTop: 4,
  },
  placeholderNote: {
    fontSize: 10,
    color: '#95a5a6',
    marginTop: 8,
    textAlign: 'center',
    fontFamily: 'monospace',
  },
  insightsSection: {
    backgroundColor: 'white',
    padding: 15,
    marginBottom: 10,
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  insightText: {
    fontSize: 14,
    color: '#34495e',
    marginLeft: 8,
    flex: 1,
  },
  interpretationSection: {
    backgroundColor: '#f8f9fa',
    padding: 15,
    marginBottom: 10,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#3498db',
  },
  interpretationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  interpretationText: {
    fontSize: 13,
    color: '#34495e',
    marginLeft: 8,
    flex: 1,
    lineHeight: 18,
  },
  interpretationBold: {
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  fileInfoSection: {
    backgroundColor: 'white',
    padding: 15,
    marginBottom: 10,
  },
  fileInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  fileInfoText: {
    fontSize: 14,
    color: '#34495e',
    marginLeft: 8,
  },
  actionsSection: {
    flexDirection: 'row',
    paddingHorizontal: 15,
    paddingBottom: 20,
    gap: 10,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 8,
  },
  actionButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  actionButtonSecondary: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 2,
    backgroundColor: 'white',
  },
  actionButtonSecondaryText: {
    fontSize: 14,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  colorScaleContainer: {
    backgroundColor: 'white',
    padding: 15,
    marginBottom: 10,
  },
  colorScaleTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
  },
  colorScaleBar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  colorScaleItem: {
    alignItems: 'center',
    marginBottom: 8,
    width: '23%',
  },
  colorBox: {
    width: 20,
    height: 20,
    borderRadius: 3,
    marginBottom: 4,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  colorLabel: {
    fontSize: 10,
    color: '#34495e',
    textAlign: 'center',
    lineHeight: 12,
  },
  colorValue: {
    fontSize: 9,
    color: '#7f8c8d',
    fontWeight: 'bold',
  },
  zoomIndicator: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    backgroundColor: 'rgba(0,0,0,0.7)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 15,
    flexDirection: 'row',
    alignItems: 'center',
  },
  zoomText: {
    color: 'white',
    fontSize: 12,
    marginLeft: 4,
  },
  zoomModalContainer: {
    flex: 1,
    backgroundColor: 'black',
    justifyContent: 'center',
    alignItems: 'center',
  },
  zoomCloseButton: {
    position: 'absolute',
    top: 50,
    right: 20,
    zIndex: 1000,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
    padding: 10,
  },
  colorScaleOverlay: {
    position: 'absolute',
    top: 10,
    left: 10,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: 6,
    padding: 6,
  },
  colorScaleOverlayZoom: {
    position: 'absolute',
    top: 100,
    left: 20,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: 6,
    padding: 6,
    zIndex: 1000,
  },
  colorScaleContainer: {
    alignItems: 'center',
  },
  colorScaleBar: {
    width: 20,
    height: 80,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  colorScaleLabelSide: {
    fontSize: 8,
    color: '#2c3e50',
    fontWeight: 'bold',
    marginVertical: 2,
  },
});

export default InteractiveMapView;